import { EnvironmentProviders } from '@angular/core';
import { provideTransloco } from '@jsverse/transloco';
import { isDebugBuild } from '../debug-flag';
import { TranslocoHttpLoader } from './transloco-http.loader';
import { APP_SEGMENTS, effectiveLocale } from './segment-locale';
import { APP_LANGS } from './language.service';

/**
 * ADR-0058: runtime i18n provider for the customer SPA (toybank-app).
 *
 *  - availableLangs includes the four user languages and five internal segment variants per language.
 *  - defaultLang 'en' -- English is the front-door default across ToyBank.
 *  - fallbackLang 'en' + useFallbackTranslation -- missing localized keys fall back
 *    to the English string rather than rendering the raw key.
 *  - reRenderOnLangChange true -- the toggle re-renders every transloco pipe/directive live,
 *    no reload (this app is zoneless + signal-based; transloco drives change detection itself).
 *  - prodMode = !isDebugBuild -- production/bff bundle runs Transloco in prod mode (no missing-key
 *    console warnings); debug/sandbox/development builds log missing keys to help the follow-up
 *    per-feature localization fan-out find gaps.
 *
 * SCOPED loading: base feature scopes resolve to assets/i18n/<scope>/<lang>.json;
 * sparse segment copy comes from one shared document per segment and language.
 */
export function provideAppTransloco(): EnvironmentProviders[] {
  return provideTransloco({
    config: {
      availableLangs: APP_LANGS.flatMap(lang => APP_SEGMENTS.map(segment => effectiveLocale(lang, segment))),
      defaultLang: 'en',
      fallbackLang: 'en',
      reRenderOnLangChange: true,
      prodMode: !isDebugBuild,
      missingHandler: {
        // In dev, log missing keys so the follow-up fan-out can find untranslated surfaces.
        logMissingKey: isDebugBuild,
        useFallbackTranslation: true,
        allowEmpty: false,
      },
    },
    loader: TranslocoHttpLoader,
  });
}
